import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Chip, Spinner } from "@heroui/react";
import { ArrowLeft, Check, Flame, RotateCcw, Star, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { GameChart } from "./GameChart";
import { SessionData, buildQuestion, type Built } from "./data";
import { isCorrect, type Answer } from "./grade";
import { dateText, price as fmtPrice } from "./helpers";
import { makeRng, randomSeed } from "./rng";
import {
  LEVEL_META, PASS_SCORE, SESSION_SIZE, applySession, isUnlocked, pickGames, pointsFor, readProgress, saveProgress, starsFor,
  type Progress,
} from "./session";
import { LEVELS, type ChartSpec, type GameDef, type Level, type Question } from "./types";

interface Slot {
  game: GameDef;
  built: Built | null;
  /** Still building (or waiting for its data). */
  loading: boolean;
}

interface Played {
  game: GameDef;
  built: Built;
  answer: Answer;
  correct: boolean;
  points: number;
}

/** Games swapped in for one that can't make a question (no data, or only close calls). */
const MAX_SWAPS = 3;

/**
 * Builds the session's questions in the background — all at once, so the
 * shared chart windows load in parallel and the slow signal window starts
 * early — and swaps out any game that can't make a fair question.
 */
function useSessionSlots(pool: GameDef[], progress: Progress, seed: number): Slot[] {
  const first = useMemo(() => pickGames(pool, SESSION_SIZE, makeRng(seed), progress.games), [pool, seed]); // eslint-disable-line react-hooks/exhaustive-deps
  const [slots, setSlots] = useState<Slot[]>(() => first.map(game => ({ game, built: null, loading: true })));

  useEffect(() => {
    setSlots(first.map(game => ({ game, built: null, loading: true })));
    const data = new SessionData(makeRng(seed ^ 0x51ed270b));
    let cancelled = false;
    const used = new Set(first.map(g => g.id));
    const spares = () => pool.filter(g => !used.has(g.id) && !g.signals);

    const build = async (i: number, game: GameDef, swaps: number) => {
      const built = await buildQuestion(game, data, makeRng(seed + 7919 * (i + 1) + 104729 * swaps));
      if (cancelled) return;
      if (built) {
        setSlots(s => s.map((x, k) => (k === i ? { game, built, loading: false } : x)));
        return;
      }
      const next = swaps < MAX_SWAPS ? makeRng(seed + i + swaps).shuffle(spares())[0] : undefined;
      if (!next) {
        setSlots(s => s.map((x, k) => (k === i ? { game, built: null, loading: false } : x)));
        return;
      }
      used.add(next.id);
      setSlots(s => s.map((x, k) => (k === i ? { game: next, built: null, loading: true } : x)));
      await build(i, next, swaps + 1);
    };
    first.forEach((g, i) => void build(i, g, 0));
    return () => {
      cancelled = true;
      data.close();
    };
  }, [first, pool, seed]);

  return slots;
}

export function GameSession({ level, pool, onExit }: { level: Level; pool: GameDef[]; onExit: () => void }) {
  const [seed, setSeed] = useState(randomSeed);
  const [progress] = useState(readProgress);
  const slots = useSessionSlots(pool, progress, seed);
  const [index, setIndex] = useState(0);
  const [played, setPlayed] = useState<Played[]>([]);
  const [current, setCurrent] = useState<{ answer: Answer; correct: boolean; points: number } | null>(null);
  const [finished, setFinished] = useState<{ before: Progress; after: Progress } | null>(null);

  // Questions that couldn't be built at all are skipped, so a session can be shorter than 10.
  const playable = slots.filter(s => s.loading || s.built);
  const slot = playable[index];
  const streak = useMemo(() => {
    let n = 0;
    for (let i = played.length - 1; i >= 0 && played[i]!.correct; i--) n++;
    return n;
  }, [played]);
  const score = played.reduce((s, p) => s + p.points, 0);

  const submit = useCallback((answer: Answer) => {
    if (!slot?.built || current) return;
    const correct = isCorrect(slot.built.question, answer);
    const points = pointsFor(correct, streak);
    setCurrent({ answer, correct, points });
    setPlayed(p => [...p, { game: slot.game, built: slot.built!, answer, correct, points }]);
  }, [slot, current, streak]);

  const next = useCallback(() => {
    if (!current) return;
    setCurrent(null);
    if (index + 1 < playable.length) {
      setIndex(index + 1);
      return;
    }
    const before = readProgress();
    const after = applySession(before, level, played.map(p => ({ gameId: p.game.id, correct: p.correct, points: p.points })), Date.now());
    saveProgress(after);
    setFinished({ before, after });
  }, [current, index, playable.length, level, played]);

  const restart = () => {
    setSeed(randomSeed());
    setIndex(0);
    setPlayed([]);
    setCurrent(null);
    setFinished(null);
  };

  if (finished) {
    return <Summary level={level} played={played} before={finished.before} after={finished.after} onAgain={restart} onExit={onExit} />;
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 pb-16">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <Button size="sm" variant="ghost" onPress={onExit}>
          <ArrowLeft className="size-4" /> Quit
        </Button>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted">{LEVEL_META[level].label}</span>
          <span className="num font-semibold text-foreground">{score} pts</span>
          <span className={cn("flex items-center gap-1 num font-semibold", streak >= 2 ? "text-warning" : "text-muted")} title="Right answers in a row">
            <Flame className="size-4" /> {streak}
          </span>
        </div>
      </header>

      <ProgressDots total={playable.length} index={index} played={played} />

      {!slot ? (
        <Alert status="danger">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>No questions could be made</Alert.Title>
            <Alert.Description>The chart data didn't load. Check the backend is running, then try again.</Alert.Description>
          </Alert.Content>
        </Alert>
      ) : !slot.built ? (
        <LoadingCard game={slot.game} />
      ) : (
        <QuestionCard
          key={`${seed}-${index}`}
          number={index + 1}
          total={playable.length}
          game={slot.game}
          built={slot.built}
          result={current}
          streak={streak}
          onSubmit={submit}
          onNext={next}
          last={index + 1 >= playable.length}
        />
      )}
    </div>
  );
}

function ProgressDots({ total, index, played }: { total: number; index: number; played: Played[] }) {
  return (
    <div className="flex gap-1" aria-label={`Question ${index + 1} of ${total}`}>
      {Array.from({ length: total }, (_, i) => {
        const p = played[i];
        return (
          <span
            key={i}
            className={cn(
              "h-1.5 flex-1 rounded-full",
              p ? (p.correct ? "bg-profit" : "bg-loss") : i === index ? "bg-brand" : "bg-default",
            )}
          />
        );
      })}
    </div>
  );
}

function LoadingCard({ game }: { game: GameDef }) {
  return (
    <div className="flex min-h-80 flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-surface p-6 text-center">
      <Spinner size="md" />
      <p className="text-sm font-medium text-foreground">{game.title}</p>
      <p className="max-w-sm text-xs text-muted">
        {game.signals
          ? "Loading the engine's signals for a random past month. The first time this can take about 15 seconds."
          : "Loading a fresh chart from a random coin and day…"}
      </p>
    </div>
  );
}

interface CardProps {
  number: number;
  total: number;
  game: GameDef;
  built: Built;
  result: { answer: Answer; correct: boolean; points: number } | null;
  streak: number;
  onSubmit: (a: Answer) => void;
  onNext: () => void;
  last: boolean;
}

function QuestionCard({ number, total, game, built, result, onSubmit, onNext, last }: CardProps) {
  const q = built.question;
  const revealed = result !== null;
  const [picked, setPicked] = useState<number | null>(null);
  // A price question's chart also shows, once answered, the range that counts and the ideal price.
  const chart = useMemo((): ChartSpec | undefined => {
    if (q.kind !== "price") return q.chart;
    return {
      ...q.chart,
      answerZones: [...(q.chart.answerZones ?? []), { from: q.accept.from, to: q.accept.to, tone: "profit" }],
      answerLines: q.chart.answerLines?.length ? q.chart.answerLines : [{ price: q.answer, label: "Answer", tone: "profit" }],
    };
  }, [q]);

  // Keys: 1–5 pick an option, Enter goes on once answered.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (revealed && e.key === "Enter") {
        e.preventDefault();
        onNext();
        return;
      }
      if (!revealed && q.kind === "choice") {
        const n = Number(e.key);
        const opt = Number.isInteger(n) && n >= 1 ? q.options[n - 1] : undefined;
        if (opt) onSubmit(opt.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [revealed, q, onNext, onSubmit]);

  // A new question starts at its top; an answer scrolls its feedback into view.
  const top = useRef<HTMLElement>(null);
  const feedback = useRef<HTMLDivElement>(null);
  // Braces matter: newer browsers return a Promise from scrollIntoView, and an effect must return nothing or a cleanup.
  useEffect(() => {
    void top.current?.scrollIntoView({ block: "nearest" });
  }, []);
  useEffect(() => {
    if (revealed) void feedback.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [revealed]);

  return (
    <article ref={top} className="scroll-mt-4 space-y-4 rounded-2xl border border-border bg-surface p-4 sm:p-5">
      <div>
        <p className="text-2xs font-medium uppercase tracking-wide text-muted">
          Question <span className="num">{number}</span> of {total} · {game.title}
        </p>
        <p className="mt-0.5 text-xs text-muted">{game.skill}</p>
      </div>

      {chart && (
        <div>
          <GameChart
            spec={chart}
            revealed={revealed}
            picked={q.kind === "price" ? (result ? (result.answer as number) : picked) : null}
            onPick={q.kind === "price" && !revealed ? setPicked : undefined}
          />
          <p className="mt-1 text-2xs text-muted">
            {revealed
              ? <>{built.coin ?? "Coin"}{built.atSec ? ` · ${dateText(built.atSec)}` : ""} · the faded candles are what came next (not scored)</>
              : <>{chart.tf} chart · the date stays hidden until you answer · drag to scroll back</>}
          </p>
        </div>
      )}

      {q.facts && q.facts.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {q.facts.map(f => <FactTile key={f.label} label={f.label} value={f.value} />)}
        </div>
      )}

      <h2 className="text-base font-semibold leading-snug text-foreground sm:text-lg">{q.prompt}</h2>

      {q.kind === "choice" && <ChoiceAnswer q={q} result={result} onSubmit={onSubmit} />}
      {q.kind === "number" && <NumberAnswer q={q} result={result} onSubmit={onSubmit} />}
      {q.kind === "price" && (
        <div className="flex flex-wrap items-center gap-3">
          {!revealed && (
            <>
              <p className="text-sm text-muted">
                {picked === null ? "Click the chart at your price." : <>Your answer: <span className="num font-semibold text-foreground">{fmtPrice(picked)}</span> (click again to move it)</>}
              </p>
              <Button variant="primary" isDisabled={picked === null} onPress={() => picked !== null && onSubmit(picked)}>Check</Button>
            </>
          )}
        </div>
      )}

      {result && (
        <div ref={feedback} className="scroll-mb-4 space-y-4">
          <Feedback q={q} result={result} />
          <div className="flex justify-end">
            <Button variant="primary" onPress={onNext}>{last ? "See results" : "Next question"}</Button>
          </div>
        </div>
      )}
    </article>
  );
}

function ChoiceAnswer({ q, result, onSubmit }: { q: Extract<Question, { kind: "choice" }>; result: CardProps["result"]; onSubmit: (a: Answer) => void }) {
  return (
    <div className="grid gap-2">
      {q.options.map((o, i) => {
        const right = result !== null && o.id === q.answer;
        const wrongPick = result !== null && o.id === result.answer && !result.correct;
        return (
          <button
            key={o.id}
            type="button"
            disabled={result !== null}
            onClick={() => onSubmit(o.id)}
            className={cn(
              "flex items-center gap-3 rounded-xl border px-3.5 py-3 text-left text-sm transition-colors",
              result === null && "border-border bg-surface-secondary hover:border-brand-hover hover:bg-default focus-visible:outline-2 focus-visible:outline-focus",
              right && "border-profit bg-profit-soft text-foreground",
              wrongPick && "border-loss bg-loss-soft text-foreground",
              result !== null && !right && !wrongPick && "border-border opacity-55",
            )}
          >
            <span className="num grid size-6 shrink-0 place-items-center rounded-md bg-default text-xs text-muted">{i + 1}</span>
            <span className="flex-1">{o.label}</span>
            {right && <Check className="size-4 shrink-0 text-profit" aria-label="right answer" />}
            {wrongPick && <X className="size-4 shrink-0 text-loss" aria-label="your answer" />}
          </button>
        );
      })}
    </div>
  );
}

/** A scenario fact: numbers in the mono face, sentences in the text face. */
function FactTile({ label, value }: { label: string; value: string }) {
  const numeric = value.length <= 16 && /\d/.test(value);
  return (
    <div className={cn("rounded-lg border border-border bg-surface-secondary px-2.5 py-2", value.length > 32 && "col-span-2")}>
      <div className="text-2xs text-muted">{label}</div>
      <div className={cn("text-sm font-semibold text-foreground", numeric && "num")}>{value}</div>
    </div>
  );
}

const UNIT_BEFORE = new Set(["$"]);

function NumberAnswer({ q, result, onSubmit }: { q: Extract<Question, { kind: "number" }>; result: CardProps["result"]; onSubmit: (a: Answer) => void }) {
  const [text, setText] = useState("");
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  const value = Number(text.replace(/[,$%\s×R]/gi, "").replace(/[−–]/g, "-"));
  const valid = text.trim() !== "" && Number.isFinite(value);
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={e => {
        e.preventDefault();
        if (valid && !result) onSubmit(value);
      }}
    >
      <label className="flex items-center gap-1.5 rounded-xl border border-border bg-surface-secondary px-3 focus-within:ring-2 focus-within:ring-focus">
        {UNIT_BEFORE.has(q.unit) && <span className="text-muted">{q.unit}</span>}
        <input
          ref={ref}
          inputMode="decimal"
          aria-label="Your answer"
          disabled={result !== null}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Your answer"
          className="num h-11 w-40 bg-transparent text-base text-foreground outline-none placeholder:text-muted"
        />
        {!UNIT_BEFORE.has(q.unit) && q.unit && <span className="text-muted">{q.unit}</span>}
      </label>
      {!result && <Button type="submit" variant="primary" isDisabled={!valid}>Check</Button>}
    </form>
  );
}

function answerText(q: Question, a: Answer): string {
  if (q.kind === "choice") return q.options.find(o => o.id === a)?.label ?? String(a);
  if (q.kind === "price") return fmtPrice(a as number);
  const n = (a as number).toLocaleString("en-US", { maximumFractionDigits: Math.max(q.decimals, 2) });
  return q.unit === "$" ? `$${n}` : `${n}${q.unit ? ` ${q.unit}` : ""}`;
}

function rightText(q: Question): string {
  if (q.kind === "choice") return q.options.find(o => o.id === q.answer)?.label ?? "";
  if (q.kind === "price") return `${fmtPrice(q.answer)} (anything from ${fmtPrice(Math.min(q.accept.from, q.accept.to))} to ${fmtPrice(Math.max(q.accept.from, q.accept.to))} counts)`;
  const n = q.answer.toLocaleString("en-US", { minimumFractionDigits: q.decimals, maximumFractionDigits: q.decimals });
  return q.unit === "$" ? `$${n}` : `${n}${q.unit ? ` ${q.unit}` : ""}`;
}

function Feedback({ q, result }: { q: Question; result: NonNullable<CardProps["result"]> }) {
  const why = q.kind === "choice" && !result.correct ? q.options.find(o => o.id === result.answer)?.why : undefined;
  return (
    <div className="space-y-2">
      <Alert status={result.correct ? "success" : "danger"}>
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>{result.correct ? `Right! +${result.points} points` : "Not quite"}</Alert.Title>
          <Alert.Description className="space-y-1">
            {q.kind === "price" && (
              <span className="block">
                Answer: <span className="num font-semibold">{fmtPrice(q.answer)}</span> (anything from {fmtPrice(Math.min(q.accept.from, q.accept.to))} to {fmtPrice(Math.max(q.accept.from, q.accept.to))} counts)
                {!result.correct && <> · yours: <span className="num">{answerText(q, result.answer)}</span></>}
              </span>
            )}
            {q.kind === "number" && (
              <span className="block">
                Answer: <span className="num font-semibold">{rightText(q)}</span>
                {!result.correct && <> · yours: <span className="num">{answerText(q, result.answer)}</span></>}
              </span>
            )}
            {q.kind === "choice" && !result.correct && <span className="block">Answer: <b>{rightText(q)}</b></span>}
            {why && <span className="block">{why}</span>}
            <span className="block">{q.explain}</span>
          </Alert.Description>
        </Alert.Content>
      </Alert>
      {q.outcome && (
        <p className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted">
          <span className="font-medium text-foreground/80">What happened next (not scored):</span> {q.outcome}
        </p>
      )}
    </div>
  );
}

function Summary({ level, played, before, after, onAgain, onExit }: {
  level: Level; played: Played[]; before: Progress; after: Progress; onAgain: () => void; onExit: () => void;
}) {
  const right = played.filter(p => p.correct).length;
  const stars = starsFor(right);
  const xp = after.xp - before.xp;
  const nextLevel = LEVELS[LEVELS.indexOf(level) + 1];
  const unlockedNow = nextLevel && !isUnlocked(nextLevel, before) && isUnlocked(nextLevel, after);
  const missed = played.filter(p => !p.correct);
  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 pb-16">
      <section className="rounded-2xl border border-border bg-surface p-6 text-center">
        <p className="text-xs font-medium uppercase tracking-wide text-muted">{LEVEL_META[level].label} · session done</p>
        <p className="mt-2 text-4xl font-bold text-foreground"><span className="num">{right}</span><span className="text-muted"> / {played.length}</span></p>
        <div className="mt-2 flex justify-center gap-1" aria-label={`${stars} of 3 stars`}>
          {[1, 2, 3].map(i => <Star key={i} className={cn("size-6", i <= stars ? "fill-warning text-warning" : "text-default")} />)}
        </div>
        <p className="mt-2 text-sm text-muted">
          <span className="num font-semibold text-foreground">+{xp} XP</span>
          {right >= PASS_SCORE ? " · passed (includes a 50 XP pass bonus)" : ` · ${PASS_SCORE} right answers pass the level`}
        </p>
        {unlockedNow && nextLevel && (
          <Chip className="mt-3" color="success" variant="soft">{LEVEL_META[nextLevel].label} unlocked</Chip>
        )}
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <Button variant="primary" onPress={onAgain}><RotateCcw className="size-4" /> Play 10 more</Button>
          <Button variant="secondary" onPress={onExit}>Back to games</Button>
        </div>
      </section>

      {missed.length > 0 && (
        <section className="space-y-2 rounded-2xl border border-border bg-surface p-4">
          <h3 className="text-sm font-semibold text-foreground">Review what you missed</h3>
          <ul className="space-y-3">
            {missed.map((p, i) => (
              <li key={i} className="rounded-xl bg-surface-secondary p-3 text-sm">
                <p className="text-2xs font-medium uppercase tracking-wide text-muted">{p.game.title}</p>
                <p className="mt-0.5 font-medium text-foreground">{p.built.question.prompt}</p>
                <p className="mt-1 text-muted">Answer: <span className="text-foreground">{rightText(p.built.question)}</span></p>
                <p className="mt-1 text-muted">{p.built.question.explain}</p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
